import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "./AuthContext";

const BasketContext = createContext(null);
const STORAGE_KEY = "ufr_basket_v1";

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function BasketProvider({ children }) {
  const [items, setItems] = useState(loadInitial);
  const { user } = useAuth();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  // Keep a server-side snapshot for abandoned-basket reminders (logged-in only)
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => {
      api.post("/basket/sync", {
        items: items.map(({ competition_id, title, tickets, price_per_ticket }) => ({
          competition_id,
          title,
          tickets,
          price_per_ticket,
        })),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [items, user]);

  // item: { competition_id, title, image_url, price_per_ticket, tickets, max_tickets, answer_token }
  const addItem = useCallback((item) => {
    setItems((prev) => {
      const cap = item.max_tickets || 50;
      const idx = prev.findIndex((p) => p.competition_id === item.competition_id);
      if (idx === -1) return [...prev, item];
      // merge ticket counts (respecting the per-raffle cap), refresh answer_token
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        max_tickets: cap,
        tickets: Math.min(cap, next[idx].tickets + item.tickets),
        answer_token: item.answer_token,
      };
      return next;
    });
  }, []);

  const updateTickets = useCallback((competition_id, tickets) => {
    setItems((prev) =>
      prev.map((p) =>
        p.competition_id === competition_id
          ? { ...p, tickets: Math.max(1, Math.min(p.max_tickets || 50, tickets)) }
          : p
      )
    );
  }, []);

  const removeItem = useCallback((competition_id) => {
    setItems((prev) => prev.filter((p) => p.competition_id !== competition_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, p) => sum + p.tickets, 0);
  const total = items.reduce((sum, p) => sum + p.price_per_ticket * p.tickets, 0);

  return (
    <BasketContext.Provider
      value={{ items, addItem, updateTickets, removeItem, clear, count, total }}
    >
      {children}
    </BasketContext.Provider>
  );
}

export function useBasket() {
  return useContext(BasketContext);
}
